import { Box, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material"
import { horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { Sortable } from "lib/@components/Sortable"
import { PINNED_Z_INDEX } from "lib/@components/managed-columns/pinned-cell-styles"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { useDarkMode } from "lib/use-dark-mode"
import { Repeat } from "lib/@components/repeat"
import { AssetRegisterEntry } from "./asset-register-entry"
import { ACTION_COLUMN_WIDTH } from "routes/asset-register/plugins/regime-display/asset-view-table/asset-view-column-definitions.mjs"
import { AssetRegisterColumnHeaderCell } from "./asset-register-column-header-cell"

export function AssetRegisterTable({ register, take, setSkip }) {
    const { items, resolvedColumns, applyVisibleOrder, search, totalCount } = useBoundContext()
    const isDark = useDarkMode()
    const theme = useTheme()

    return (
        <Box
            sx={{
                flex: 1,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                width: 1,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                mt: 2,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    overflowX: "auto",
                    "& .MuiTableCell-root": {
                        whiteSpace: "nowrap",
                        py: 1.5,
                    },
                    "& .MuiTableRow-root": {
                        "&": {
                            borderBottom: "1px solid " + theme.palette.divider,
                        },
                        "&:last-of-type": {
                            borderBottom: "none",
                        },
                    },
                }}
                data-cy="asset-register-scroll-container"
            >
                <Sortable
                    items={[...resolvedColumns]}
                    id="field"
                    strategy={horizontalListSortingStrategy}
                    onOrdered={applyHeaderOrder}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow
                                sx={{
                                    "& th": {
                                        backdropFilter: "blur(5px)",
                                        position: "sticky",
                                        top: 0,
                                    },
                                    borderRadius: 1,
                                    bgcolor: isDark ? "background.paper" : "grey.100",
                                }}
                                data-cy="assets-list-header-row"
                            >
                                <Repeat
                                    list={resolvedColumns}
                                    item={<AssetRegisterColumnHeaderCell />}
                                    keyFn={keyByField}
                                />
                                {/* filler cell to occupy remaining space - avoids column stretching */}
                                <TableCell data-cy="header-filler" sx={{ width: "100%", p: 0 }} />
                                <TableCell
                                    data-cy="header-action-cover"
                                    sx={{
                                        width: ACTION_COLUMN_WIDTH,
                                        minWidth: ACTION_COLUMN_WIDTH,
                                        maxWidth: ACTION_COLUMN_WIDTH,
                                        position: "sticky",
                                        right: 0,
                                        top: 0,
                                        zIndex: PINNED_Z_INDEX.header,
                                    }}
                                />
                            </TableRow>
                        </TableHead>
                        <RequestPageRepeat
                            pageSize={take}
                            showBottom={false}
                            refreshKey={search}
                            setSkip={setSkip}
                            Component={TableBody}
                            list={items}
                            item={<AssetRegisterEntry register={register} />}
                            total={totalCount}
                        />
                    </Table>
                </Sortable>
            </Box>
        </Box>
    )

    function applyHeaderOrder(newOrder) {
        applyVisibleOrder(newOrder.map(keyByField))
    }

    function keyByField(column) {
        return column.field
    }
}
